import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { fechaCL, hoyChile } from '../utils/fecha';
import TurnoEmergencia from '../components/TurnoEmergencia';

const MESES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];
const DIAS_CORTOS = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
// Semana chilena: parte en lunes y termina en domingo
const CABECERA_SEMANA = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

const ESTADO_STYLE = {
  pendiente:    { background: '#1C1200', color: '#F59E0B' },
  completado:   { background: '#12382D', color: '#4CAF50' },
  reprogramado: { background: '#1E1228', color: '#A855F7' },
};

// Paleta por defecto de los técnicos, ordenada para que dos posiciones
// consecutivas de la lista queden lo más lejos posible en el círculo cromático
// (el mínimo entre consecutivos es 113°). La paleta anterior tenía pares a 10°
// y 26° de distancia, indistinguibles al planificar el mes.
const PALETA_TEC = [
  { hex: '#F87171', nombre: 'Coral'   },
  { hex: '#38BDF8', nombre: 'Celeste' },
  { hex: '#FBBF24', nombre: 'Ámbar'   },
  { hex: '#A78BFA', nombre: 'Violeta' },
  { hex: '#4ADE80', nombre: 'Verde'   },
  { hex: '#F472B6', nombre: 'Magenta' },
  { hex: '#A3E635', nombre: 'Lima'    },
];

// El fondo de las píldoras se deriva del color, así cualquier color elegido a
// mano queda legible sin tener que definirle un tono oscuro aparte.
function hexARgba(hex, alpha) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return `rgba(138,153,148,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function estiloTecnico(hex) {
  return { fg: hex, bg: hexARgba(hex, 0.18) };
}

const SIN_TEC = estiloTecnico('#8A9994');

// Máximo de comunidades por técnico y día antes de marcar sobrecarga
const MAX_COM_DIA = 2;

// Quién puede tomar una mantención en terreno. Debe coincidir con
// ROLES_TERRENO_SQL del backend; /admin/tecnicos es compartido con otras
// pestañas y devuelve a todos, así que acá se filtra en el cliente.
const ROLES_TERRENO = ['tecnico', 'admin', 'superadmin'];

function formatFecha(dateStr) {
  const d = new Date(String(dateStr).slice(0, 10) + 'T12:00:00');
  return `${DIAS_CORTOS[d.getDay()]} ${d.getDate()} de ${MESES[d.getMonth()].toLowerCase()}`;
}

function toDateStr(dateStr) {
  return String(dateStr).slice(0, 10);
}

// Nombre corto para las píldoras del calendario
function nombreCorto(comunidad) {
  return String(comunidad || '').replace(/^(COMUNIDAD |EDIFICIO |CONDOMINIO )/i, '');
}

// Resume la lista de equipos de un día: consecutivos como rango (1-3),
// sueltos separados por coma (1,4). Mantiene corta la píldora del calendario.
function resumirEquipos(equipos) {
  const nums = equipos.map(Number).filter(n => !isNaN(n)).sort((a, b) => a - b);
  if (nums.length !== equipos.length || !nums.length) return equipos.join(',');
  const tramos = [];
  let ini = nums[0], prev = nums[0];
  for (let i = 1; i <= nums.length; i++) {
    if (nums[i] === prev + 1) { prev = nums[i]; continue; }
    tramos.push(ini === prev ? `${ini}` : `${ini}-${prev}`);
    ini = prev = nums[i];
  }
  return tramos.join(',');
}

// En pantalla grande el calendario se despliega a ancho completo con celdas
// altas; bajo 900px vuelve al layout móvil de una columna.
function useEsEscritorio(min = 900) {
  const consultar = () =>
    typeof window !== 'undefined' && window.matchMedia(`(min-width:${min}px)`).matches;
  const [es, setEs] = useState(consultar);
  useEffect(() => {
    const mq = window.matchMedia(`(min-width:${min}px)`);
    const onCambio = e => setEs(e.matches);
    mq.addEventListener('change', onCambio);
    setEs(mq.matches);
    return () => mq.removeEventListener('change', onCambio);
  }, [min]);
  return es;
}

export default function Planificacion() {
  const navigate  = useNavigate();
  const { tecnico: user } = useAuth();
  const esAdmin   = ['admin', 'superadmin'].includes(user?.rol);
  const esPC      = useEsEscritorio();

  const now = new Date();
  const [mes,  setMes]  = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [items, setItems] = useState([]);
  const [tablero, setTablero] = useState(null);
  const [sinCobertura, setSinCobertura] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTecnico, setFiltroTecnico] = useState('');
  const [tecnicos, setTecnicos] = useState([]);
  const [vista, setVista] = useState(esAdmin ? 'calendario' : 'lista');
  const [generando, setGenerando] = useState(false);
  const [advertencias, setAdvertencias] = useState([]);
  const [editandoId, setEditandoId] = useState(null);
  const [editFecha, setEditFecha] = useState('');
  const [importando, setImportando] = useState(false);

  // Estado del modo pincel
  const [tecnicoSel, setTecnicoSel]     = useState(null);   // id del técnico activo
  const [comunidadSel, setComunidadSel] = useState(null);   // comunidad "en mano"
  const [busquedaCom, setBusquedaCom]   = useState('');     // buscador del panel
  const [pendientes, setPendientes]     = useState({});     // 'comunidad|fecha' → cambio
  const [guardando, setGuardando]       = useState(false);

  // Avisos de programación por correo
  const [modalAvisos, setModalAvisos] = useState(false);
  const [avisos, setAvisos]           = useState(null);
  const [avisosSel, setAvisosSel]     = useState([]);
  const [horaInicio, setHoraInicio]   = useState('09:00');
  const [horaFin, setHoraFin]         = useState('16:00');
  const [enviandoAvisos, setEnviando] = useState(false);
  const [vistaPrevia, setVistaPrevia] = useState(null);

  // Ajuste manual del reparto de equipos entre los días
  const [modalReparto, setModalReparto] = useState(null);
  const [guardandoRep, setGuardandoRep] = useState(false);

  // Editor de colores de los técnicos
  const [modalColores, setModalColores] = useState(false);

  // La pantalla principal es solo lectura: el mes a ancho completo. Las
  // opciones de modificación aparecen al entrar en modo edición.
  const [modoEdicion, setModoEdicion] = useState(false);

  // Chequeo previo a publicar
  const [revision, setRevision]           = useState(null);
  const [modalRevision, setModalRevision] = useState(false);
  const [cargandoRev, setCargandoRev]     = useState(false);
  const [publicando, setPublicando]       = useState(false);

  // Cobertura: comunidades sin visita y su justificación
  const [cobertura, setCobertura]           = useState(null);
  const [modalCobertura, setModalCobertura] = useState(false);
  const [motivoEdit, setMotivoEdit]         = useState({});
  const [motivoGuardando, setMotivoGuardando] = useState(null);

  // Técnico responsable por comunidad (lo que usa "Generar")
  const [modalResponsables, setModalResponsables] = useState(false);

  const editRef    = useRef(null);
  const fileImport = useRef(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      // /cobertura y /tablero son solo admin: pedirlas como técnico devuelve 403
      // y tumbaría el Promise.all, dejando al técnico sin su ruta.
      const peticiones = [api.get('/planificacion/items', { params: { mes, anio } })];
      if (esAdmin) {
        peticiones.push(
          api.get('/planificacion/cobertura', { params: { mes, anio } }),
          api.get('/planificacion/tablero',   { params: { mes, anio } }),
        );
      }

      const [rItems, rCob, rTab] = await Promise.all(peticiones);
      setItems(rItems.data);
      if (rCob) {
        setSinCobertura(rCob.data.sin_cobertura || []);
        setCobertura(rCob.data);
      }
      if (rTab) setTablero(rTab.data);
      setPendientes({});
    } catch {
      // sin conexión
    } finally {
      setLoading(false);
    }
  }, [mes, anio, esAdmin]);

  useEffect(() => { cargar(); }, [cargar]);

  // Cargar técnicos para el filtro (solo admin)
  useEffect(() => {
    if (!esAdmin) return;
    api.get('/admin/tecnicos')
      .then(r => setTecnicos(
        r.data.filter(t => t.activo && ROLES_TERRENO.includes(t.rol))
      ))
      .catch(() => {});
  }, [esAdmin]);

  // Cerrar editor de fecha al hacer clic fuera
  useEffect(() => {
    if (!editandoId) return;
    const handler = (e) => {
      if (editRef.current && !editRef.current.contains(e.target)) setEditandoId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editandoId]);

  // Avisar antes de salir con cambios sin guardar
  const nPendientes = Object.keys(pendientes).length;
  useEffect(() => {
    if (!nPendientes) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [nPendientes]);

  const cambiarMes = (delta) => {
    if (nPendientes && !window.confirm(
      `Tienes ${nPendientes} cambio(s) sin guardar. ¿Descartarlos y cambiar de mes?`
    )) return;
    setPendientes({});
    if (delta < 0) {
      if (mes === 1) { setMes(12); setAnio(a => a - 1); } else setMes(m => m - 1);
    } else {
      if (mes === 12) { setMes(1); setAnio(a => a + 1); } else setMes(m => m + 1);
    }
  };

  // ── Color por técnico ────────────────────────────────────────────────────────
  // El color guardado en el técnico manda; si no tiene, se usa el de la paleta
  // según su posición en la lista.
  const hexDeTecnico = useCallback((tecnicoId) => {
    if (!tecnicoId || !tablero) return null;
    const idx = tablero.tecnicos.findIndex(t => t.id === tecnicoId);
    if (idx === -1) return null;
    return tablero.tecnicos[idx].color || PALETA_TEC[idx % PALETA_TEC.length].hex;
  }, [tablero]);

  const colorDeTecnico = useCallback((tecnicoId) => {
    const hex = hexDeTecnico(tecnicoId);
    return hex ? estiloTecnico(hex) : SIN_TEC;
  }, [hexDeTecnico]);

  // ── Asignaciones con los cambios pendientes aplicados ────────────────────────
  // Se muestra el resultado optimista; el servidor recibe el lote al guardar.
  const asignaciones = useMemo(() => {
    if (!tablero) return [];
    const mapa = new Map();
    tablero.asignaciones.forEach(a => {
      mapa.set(`${a.comunidad}|${a.fecha}`, { ...a });
    });
    Object.values(pendientes).forEach(c => {
      const key = `${c.comunidad}|${c.fecha}`;
      if (c.accion === 'del') {
        mapa.delete(key);
      } else {
        const prev = mapa.get(key);
        const com  = tablero.comunidades.find(x => x.nombre === c.comunidad);
        mapa.set(key, {
          comunidad:     c.comunidad,
          fecha:         c.fecha,
          tecnico_id:    c.tecnico_id,
          tecnico_nombre: tablero.tecnicos.find(t => t.id === c.tecnico_id)?.nombre || null,
          n_ascensores:  prev?.n_ascensores ?? com?.n_ascensores ?? 0,
          n_completados: prev?.n_completados ?? 0,
          pendiente:     true,
        });
      }
    });
    return [...mapa.values()];
  }, [tablero, pendientes]);

  // fecha → asignaciones de ese día
  const porDia = useMemo(() => {
    const m = {};
    asignaciones.forEach(a => {
      if (!m[a.fecha]) m[a.fecha] = [];
      m[a.fecha].push(a);
    });
    Object.values(m).forEach(arr => arr.sort((x, y) => x.comunidad.localeCompare(y.comunidad)));
    return m;
  }, [asignaciones]);

  // Comunidades marcadas para notificar que aún no reciben el aviso del mes
  const nAvisoPendiente = useMemo(() => {
    if (!tablero) return 0;
    const conVisitas = new Set(asignaciones.map(a => a.comunidad));
    return tablero.comunidades.filter(
      c => c.notificar_email && !c.avisado_en && conVisitas.has(c.nombre)
    ).length;
  }, [tablero, asignaciones]);

  // comunidad → nº de días asignados (para el contador n/dias_mantencion)
  const diasPorComunidad = useMemo(() => {
    const m = {};
    asignaciones.forEach(a => { m[a.comunidad] = (m[a.comunidad] || 0) + 1; });
    return m;
  }, [asignaciones]);

  // 'tecnicoId|fecha' → nº de comunidades, para detectar sobrecarga
  const cargaTecnicoDia = useMemo(() => {
    const m = {};
    asignaciones.forEach(a => {
      if (!a.tecnico_id) return;
      const k = `${a.tecnico_id}|${a.fecha}`;
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [asignaciones]);

  // Carga del mes por técnico, para detectar desbalances sin contar píldoras.
  // Sale de las asignaciones que ya están en memoria: no pide nada al servidor.
  const cargaPorTecnico = useMemo(() => {
    const m = new Map();
    asignaciones.forEach(a => {
      const k = a.tecnico_id || 0;
      if (!m.has(k)) {
        m.set(k, {
          tecnico_id: a.tecnico_id || null,
          nombre:     a.tecnico_nombre || 'Sin asignar',
          visitas:    0,
          dias:       new Set(),
          hechas:     0,
        });
      }
      const e = m.get(k);
      e.visitas += 1;
      e.dias.add(a.fecha);
      if (a.n_completados > 0) e.hechas += 1;
    });
    return [...m.values()]
      .map(e => ({ ...e, dias: e.dias.size }))
      .sort((a, b) => (a.tecnico_id === null) - (b.tecnico_id === null) || b.visitas - a.visitas);
  }, [asignaciones]);

  // Comunidades sin técnico responsable: son las que "Generar" dejaría sin asignar
  const nSinResponsable = useMemo(
    () => tablero ? tablero.comunidades.filter(c => !c.responsable_id).length : 0,
    [tablero]
  );

  // Colores que quedaron asignados a más de un técnico
  const coloresRepetidos = useMemo(() => {
    if (!tablero) return new Set();
    const cuenta = {};
    tablero.tecnicos.forEach(t => {
      const h = (hexDeTecnico(t.id) || '').toUpperCase();
      if (h) cuenta[h] = (cuenta[h] || 0) + 1;
    });
    return new Set(Object.keys(cuenta).filter(h => cuenta[h] > 1));
  }, [tablero, hexDeTecnico]);

  // Comunidades sin visita que todavía no tienen justificación
  const nCobPendiente = (cobertura?.pendientes || []).length;

  // Avance del mes, para el encabezado de la vista de lectura
  const avanceMes = useMemo(() => {
    const total  = asignaciones.length;
    const hechas = asignaciones.filter(a => a.n_completados > 0).length;
    const sinTec = asignaciones.filter(a => !a.tecnico_id).length;
    return { total, hechas, sinTec };
  }, [asignaciones]);

  const hoy = hoyChile();

  // ── Modo edición ────────────────────────────────────────────────────────────
  const salirEdicion = () => {
    if (nPendientes && !window.confirm(
      `Tienes ${nPendientes} cambio(s) sin guardar. ¿Descartarlos y salir de edición?`
    )) return;
    setPendientes({});
    setComunidadSel(null);
    setBusquedaCom('');
    setModoEdicion(false);
  };

  // ── Clic en un día del calendario ───────────────────────────────────────────
  const clicDia = (fecha, clicable) => {
    if (!modoEdicion) return;
    if (!comunidadSel || !clicable) return;

    const key       = `${comunidadSel}|${fecha}`;
    const existente = asignaciones.find(a => a.comunidad === comunidadSel && a.fecha === fecha);
    // Estado en el servidor, para no contar como "pendiente" un clic que deja
    // el día exactamente como ya estaba guardado.
    const enServidor = tablero?.asignaciones.find(
      a => a.comunidad === comunidadSel && a.fecha === fecha
    );

    let cambio;
    if (existente && tecnicoSel && existente.tecnico_id !== tecnicoSel) {
      // Ya asignada con otro técnico y hay uno activo → reasignar, no borrar
      cambio = { accion: 'add', comunidad: comunidadSel, fecha, tecnico_id: tecnicoSel };
    } else if (existente) {
      if (existente.n_completados > 0) {
        alert('Esa visita ya tiene comprobantes enviados: no se puede quitar del calendario.');
        return;
      }
      cambio = { accion: 'del', comunidad: comunidadSel, fecha };
    } else {
      cambio = { accion: 'add', comunidad: comunidadSel, fecha, tecnico_id: tecnicoSel };
    }

    const esNoOp =
      (cambio.accion === 'del' && !enServidor) ||
      (cambio.accion === 'add' && enServidor &&
        (enServidor.tecnico_id || null) === (cambio.tecnico_id || null));

    setPendientes(p => {
      const sig = { ...p };
      if (esNoOp) delete sig[key]; else sig[key] = cambio;
      return sig;
    });
  };

  const guardarCambios = async () => {
    const cambios = Object.values(pendientes);
    if (!cambios.length) return;
    setGuardando(true);
    try {
      const r = await api.post('/planificacion/asignaciones', { mes, anio, cambios });
      const adv = r.data.advertencias || [];
      setAdvertencias(adv);
      await cargar();
    } catch (err) {
      alert('Error al guardar: ' + (err.response?.data?.error || err.message));
    } finally {
      setGuardando(false);
    }
  };

  // ── Color de un técnico ─────────────────────────────────────────────────────
  // color = null vuelve al color automático de la paleta.
  const cambiarColor = async (tecnicoId, color) => {
    const antes = tablero;
    // Optimista: el calendario completo se repinta al instante
    setTablero(t => t && ({
      ...t,
      tecnicos: t.tecnicos.map(x => x.id === tecnicoId ? { ...x, color } : x),
    }));
    try {
      await api.put(`/planificacion/tecnicos/${tecnicoId}/color`, { color });
    } catch (err) {
      setTablero(antes);
      alert('No se pudo guardar el color: ' + (err.response?.data?.error || err.message));
    }
  };

  // ── Técnico responsable de la comunidad ─────────────────────────────────────
  // Es lo que usa "⚡ Generar" para asignar; sin esto el mes nace sin técnicos.
  const cambiarResponsable = async (comunidad, tecnicoId) => {
    const antes = tablero;
    setTablero(t => t && ({
      ...t,
      comunidades: t.comunidades.map(c =>
        c.nombre === comunidad ? { ...c, responsable_id: tecnicoId } : c),
    }));
    try {
      await api.put(`/planificacion/comunidades/${encodeURIComponent(comunidad)}/tecnico`,
        { tecnico_id: tecnicoId });
    } catch (err) {
      setTablero(antes);
      alert('No se pudo guardar: ' + (err.response?.data?.error || err.message));
    }
  };

  // ── Justificar una comunidad sin visita ─────────────────────────────────────
  const abrirCobertura = () => {
    const just = cobertura?.justificaciones || {};
    const inicial = {};
    (cobertura?.sin_cobertura || []).forEach(c => { inicial[c] = just[c]?.motivo || ''; });
    setMotivoEdit(inicial);
    setModalCobertura(true);
  };

  const guardarMotivo = async (comunidad) => {
    const motivo = (motivoEdit[comunidad] || '').trim();
    setMotivoGuardando(comunidad);
    try {
      await api.put('/planificacion/cobertura/justificar', { mes, anio, comunidad, motivo });
      // Refleja el cambio sin recargar todo el tablero
      setCobertura(c => {
        if (!c) return c;
        const just = { ...(c.justificaciones || {}) };
        if (motivo) just[comunidad] = { motivo, por: user?.nombre || null, fecha: new Date().toISOString() };
        else delete just[comunidad];
        return {
          ...c,
          justificaciones: just,
          pendientes: (c.sin_cobertura || []).filter(x => !just[x]),
        };
      });
    } catch (err) {
      alert('No se pudo guardar: ' + (err.response?.data?.error || err.message));
    } finally {
      setMotivoGuardando(null);
    }
  };

  // ── Avisos de programación al cliente ───────────────────────────────────────
  const toggleNotificar = async (nombre, valor) => {
    // Optimista: la fila del panel responde al instante
    setTablero(t => t && ({
      ...t,
      comunidades: t.comunidades.map(c =>
        c.nombre === nombre ? { ...c, notificar_email: valor } : c),
    }));
    try {
      await api.put(`/planificacion/comunidades/${encodeURIComponent(nombre)}/notificar`,
        { notificar_email: valor });
    } catch (err) {
      alert('No se pudo guardar: ' + (err.response?.data?.error || err.message));
      cargar();
    }
  };

  const abrirAvisos = async () => {
    if (nPendientes) { alert('Guarda los cambios pendientes antes de avisar.'); return; }
    try {
      const r = await api.get('/planificacion/avisos', { params: { mes, anio } });
      setAvisos(r.data);
      // Preselecciona las que aún no reciben aviso y tienen correo
      setAvisosSel(
        (r.data.comunidades || [])
          .filter(c => !c.avisado_en && !c.sin_email)
          .map(c => c.nombre)
      );
      setVistaPrevia(null);
      setModalAvisos(true);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const enviarAvisos = async (preview) => {
    if (!avisosSel.length) { alert('Selecciona al menos una comunidad.'); return; }
    if (!preview && !window.confirm(
      `¿Enviar el aviso de programación a ${avisosSel.length} comunidad(es)?\n\n` +
      'El correo sale a los mismos destinatarios que reciben los comprobantes.'
    )) return;

    setEnviando(true);
    try {
      const r = await api.post('/planificacion/avisos/enviar', {
        mes, anio, comunidades: avisosSel,
        hora_inicio: horaInicio, hora_fin: horaFin, preview,
      });
      if (preview) {
        const ok = r.data.resultados.find(x => x.ok && x.preview);
        if (ok) setVistaPrevia({ comunidad: ok.comunidad, html: ok.preview, destinatarios: ok.destinatarios });
        else alert('No se pudo generar la vista previa: ' + (r.data.resultados[0]?.motivo || ''));
      } else {
        const fallidos = r.data.resultados.filter(x => !x.ok);
        let msg = `✅ ${r.data.enviados} aviso(s) enviado(s)`;
        if (fallidos.length) {
          msg += `\n\n⚠ No se enviaron:\n` +
            fallidos.map(f => `· ${f.comunidad}: ${f.motivo}`).join('\n');
        }
        alert(msg);
        setModalAvisos(false);
        cargar();
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setEnviando(false);
    }
  };

  // ── Reparto de equipos entre los días ───────────────────────────────────────
  const abrirReparto = async (comunidad) => {
    if (nPendientes) { alert('Guarda los cambios pendientes antes de ajustar el reparto.'); return; }
    try {
      const r = await api.get('/planificacion/reparto', { params: { mes, anio, comunidad } });
      if (!r.data.dias.length) {
        alert(`${comunidad} no tiene días asignados este mes.`);
        return;
      }
      setModalReparto(r.data);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleAscensorDia = (fecha, ascensorId) => {
    setModalReparto(m => ({
      ...m,
      dias: m.dias.map(d => {
        if (d.fecha !== fecha) return d;
        const tiene = d.ascensor_ids.includes(ascensorId);
        if (tiene && d.completados.includes(ascensorId)) return d; // ya ejecutado
        return {
          ...d,
          ascensor_ids: tiene
            ? d.ascensor_ids.filter(x => x !== ascensorId)
            : [...d.ascensor_ids, ascensorId],
        };
      }),
    }));
  };

  const guardarReparto = async (automatico) => {
    setGuardandoRep(true);
    try {
      const r = await api.put('/planificacion/reparto', {
        mes, anio, comunidad: modalReparto.comunidad,
        dias: modalReparto.dias, automatico,
      });
      const adv = r.data.advertencias || [];
      if (adv.length) setAdvertencias(adv);
      setModalReparto(null);
      cargar();
    } catch (err) {
      alert('Error al guardar: ' + (err.response?.data?.error || err.message));
    } finally {
      setGuardandoRep(false);
    }
  };

  // ── Publicar / despublicar ──────────────────────────────────────────────────
  const calEstado = tablero?.calendario?.estado || null;

  const aplicarEstado = async (nuevo) => {
    const cal = tablero?.calendario;
    if (!cal) return;
    setPublicando(true);
    try {
      await api.put(`/planificacion/calendarios/${cal.id}/estado`, { estado: nuevo });
      setModalRevision(false);
      await cargar();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setPublicando(false);
    }
  };

  // Publicar pasa por el chequeo previo; despublicar es directo.
  const togglePublicar = async () => {
    const cal = tablero?.calendario;
    if (!cal) { alert('Aún no hay nada planificado en este mes.'); return; }
    if (nPendientes) { alert('Guarda los cambios pendientes antes de publicar.'); return; }

    if (cal.estado === 'publicado') {
      if (!window.confirm(
        `¿Volver a borrador?\n\nLos técnicos dejarán de ver ${MESES[mes-1]} ${anio} en la app.`
      )) return;
      return aplicarEstado('borrador');
    }

    setCargandoRev(true);
    try {
      const r = await api.get('/planificacion/revision', { params: { mes, anio } });
      setRevision(r.data);
      setModalRevision(true);
    } catch (err) {
      alert('Error al revisar: ' + (err.response?.data?.error || err.message));
    } finally {
      setCargandoRev(false);
    }
  };

  // ── Copiar mes anterior ─────────────────────────────────────────────────────
  const copiarMesAnterior = async (forzar = false) => {
    const dMes  = mes === 1 ? 12 : mes - 1;
    const dAnio = mes === 1 ? anio - 1 : anio;
    if (!forzar && !window.confirm(
      `¿Copiar la planificación de ${MESES[dMes-1]} ${dAnio} a ${MESES[mes-1]} ${anio}?\n\n` +
      'Cada visita se mueve al mismo día de la semana equivalente (el 2º martes sigue siendo el 2º martes).'
    )) return;

    setGenerando(true);
    try {
      const r = await api.post('/planificacion/copiar-mes', { mes, anio, forzar });
      setAdvertencias(r.data.advertencias || []);
      alert(`✅ ${r.data.insertados} visitas copiadas desde ${r.data.desde}`);
      cargar();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al copiar';
      if (!forzar && err.response?.status === 409) {
        setGenerando(false);
        // eslint-disable-next-line no-restricted-globals
        if (confirm(`${msg}\n\n¿Reemplazarlas?`)) copiarMesAnterior(true);
        return;
      }
      alert(msg);
    } finally {
      setGenerando(false);
    }
  };

  // ── Generar ─────────────────────────────────────────────────────────────────
  const doGenerar = async (forzar) => {
    setGenerando(true);
    try {
      const r = await api.post('/planificacion/generar', { mes, anio, forzar });
      setAdvertencias(r.data.advertencias || []);
      alert(`✅ ${r.data.generados} ítems generados`);
      cargar();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al generar';
      if (!forzar && err.response?.status === 409) {
        // eslint-disable-next-line no-restricted-globals
        if (confirm(`${msg}\n\n¿Forzar regeneración?`)) {
          setGenerando(false);
          doGenerar(true);
          return;
        }
      } else {
        alert(msg);
      }
    } finally {
      setGenerando(false);
    }
  };

  // ── Plantilla Excel ─────────────────────────────────────────────────────────
  const descargarPlantilla = async () => {
    try {
      const r = await api.get('/planificacion/plantilla', {
        params: { mes, anio },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `planificacion_${anio}_${String(mes).padStart(2,'0')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let msg = err.message;
      if (err.response?.data instanceof Blob) {
        try { const t = await err.response.data.text(); msg = JSON.parse(t).error || msg; } catch {}
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      }
      alert('Error al descargar: ' + msg);
    }
  };

  const cargarPlantilla = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setImportando(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('mes', mes);
      fd.append('anio', anio);
      const r = await api.post('/planificacion/importar-plantilla', fd);
      const adv = r.data.advertencias || [];
      let msg = `✅ ${r.data.insertados} ítems importados para ${MESES[mes-1]} ${anio}`;
      if (adv.length) msg += `\n\n⚠ Advertencias:\n${adv.join('\n')}`;
      alert(msg);
      setAdvertencias(adv);
      cargar();
    } catch (err) {
      alert('Error al importar: ' + (err.response?.data?.error || err.message));
    } finally {
      setImportando(false);
    }
  };

  // ── Editar fecha inline (vista lista) ───────────────────────────────────────
  const abrirEditor = (item) => {
    setEditandoId(item.id);
    setEditFecha(toDateStr(item.fecha_programada));
  };

  const guardarFecha = async (itemId) => {
    if (!editFecha) return;
    try {
      await api.patch(`/planificacion/items/${itemId}`, {
        fecha_programada: editFecha,
        estado: 'reprogramado',
        override_manual: true,
      });
      setEditandoId(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    }
  };

  // ── Filtros ─────────────────────────────────────────────────────────────────
  const itemsFiltrados = items.filter(i => {
    const matchBusq = !busqueda ||
      i.comunidad?.toLowerCase().includes(busqueda.toLowerCase()) ||
      i.label?.toLowerCase().includes(busqueda.toLowerCase());
    const matchTec = !filtroTecnico || String(i.tecnico_id) === filtroTecnico;
    return matchBusq && matchTec;
  });

  // ── Vista grilla ────────────────────────────────────────────────────────────
  const diasEnMes = Array.from({ length: new Date(anio, mes, 0).getDate() }, (_, i) => {
    const d = new Date(anio, mes - 1, i + 1);
    return { num: i + 1, dow: d.getDay() };
  });

  const tecnicosUnicos = [...new Set(items.map(i => i.tecnico_nombre || 'Sin asignar'))].sort();

  const grilla = {};
  tecnicosUnicos.forEach(t => {
    grilla[t] = {};
    diasEnMes.forEach(d => { grilla[t][d.num] = []; });
  });
  items.forEach(item => {
    const t = item.tecnico_nombre || 'Sin asignar';
    const d = new Date(toDateStr(item.fecha_programada) + 'T12:00:00').getDate();
    if (grilla[t]?.[d] && !grilla[t][d].includes(item.comunidad)) grilla[t][d].push(item.comunidad);
  });

  // ── Semanas del calendario (lunes a domingo) ────────────────────────────────
  const semanas = useMemo(() => {
    if (!tablero) return [];
    const dias = tablero.dias;
    if (!dias.length) return [];
    // Nº de huecos antes del día 1 para que la fila empiece en lunes
    const offset = (dias[0].dow + 6) % 7;
    const celdas = [...Array(offset).fill(null), ...dias];
    while (celdas.length % 7 !== 0) celdas.push(null);
    const out = [];
    for (let i = 0; i < celdas.length; i += 7) out.push(celdas.slice(i, i + 7));
    return out;
  }, [tablero]);

  // Comunidades del panel, filtradas por el buscador
  const comunidadesPanel = useMemo(() => {
    if (!tablero) return [];
    const q = busquedaCom.trim().toLowerCase();
    return tablero.comunidades.filter(c => !q || c.nombre.toLowerCase().includes(q));
  }, [tablero, busquedaCom]);

  // ── Estilos ─────────────────────────────────────────────────────────────────
  const btnNav = (color = '#0f6e56') => ({
    background: color, border: 'none', borderRadius: 8,
    padding: '6px 16px', color: 'white', fontSize: 18,
    cursor: 'pointer', fontWeight: 700,
  });
  const btnTool = (bg, fg, borde) => ({
    background: bg, border: borde || 'none', borderRadius: 8,
    padding: '7px 14px', color: fg, fontWeight: 700,
    fontSize: 13, cursor: 'pointer',
  });

  return (
    <div className={`app-container${esAdmin ? ' app-container--wide' : ''}`}>
      <div className="app-header">
        <button onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', color: 'white',
            fontSize: 15, cursor: 'pointer', marginRight: 12 }}>
          ← Volver
        </button>
        <h1 style={{ margin: 0, fontSize: 16, flex: 1 }}>
          📅 {esAdmin ? 'Planificación' : 'Mi Planificación'}
        </h1>
        {esAdmin && (
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { id: 'calendario', label: '🗓' , title: 'Calendario' },
              { id: 'grilla',     label: '⊞' , title: 'Grilla por técnico' },
              { id: 'lista',      label: '☰' , title: 'Lista' },
            ].map(v => (
              <button
                key={v.id}
                title={v.title}
                onClick={() => setVista(v.id)}
                style={{
                  background: vista === v.id ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.12)',
                  border: 'none', borderRadius: 8, padding: '5px 10px',
                  color: 'white', fontSize: 14, cursor: 'pointer',
                }}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Navegador mes/año + estado de publicación */}
      <div style={{ background: '#0f6e56', padding: '10px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button style={btnNav('rgba(255,255,255,0.2)')} onClick={() => cambiarMes(-1)}>◀</button>
        <span style={{ color: 'white', fontWeight: 700, fontSize: 16, minWidth: 150, textAlign: 'center' }}>
          {MESES[mes - 1]} {anio}
        </span>
        <button style={btnNav('rgba(255,255,255,0.2)')} onClick={() => cambiarMes(1)}>▶</button>

        {/* En lectura el estado se muestra como información; publicar vive
            dentro del modo edición, junto al resto de las acciones. */}
        {esAdmin && (modoEdicion || vista !== 'calendario' ? (
          <button
            onClick={togglePublicar}
            disabled={cargandoRev || publicando}
            title={calEstado === 'publicado'
              ? 'Los técnicos ven este mes en la app'
              : 'Los técnicos NO ven este mes hasta publicarlo'}
            style={{
              background: calEstado === 'publicado' ? '#0a4d3c' : '#F59E0B',
              border: calEstado === 'publicado' ? '1.5px solid rgba(255,255,255,0.35)' : 'none',
              borderRadius: 20, padding: '6px 14px',
              color: calEstado === 'publicado' ? '#9BE8CE' : '#1C1200',
              fontWeight: 700, fontSize: 12, cursor: 'pointer',
            }}
          >
            {cargandoRev ? 'Revisando…'
              : calEstado === 'publicado' ? '✓ Publicado' : '⚠ Borrador — Publicar'}
          </button>
        ) : (
          <span
            title={calEstado === 'publicado'
              ? 'Los técnicos ven este mes en la app'
              : 'Los técnicos NO ven este mes hasta publicarlo'}
            style={{
              background: calEstado === 'publicado' ? '#0a4d3c' : 'rgba(245,158,11,0.22)',
              border: `1.5px solid ${calEstado === 'publicado' ? 'rgba(255,255,255,0.35)' : '#F59E0B'}`,
              borderRadius: 20, padding: '5px 13px',
              color: calEstado === 'publicado' ? '#9BE8CE' : '#F59E0B',
              fontWeight: 700, fontSize: 12,
            }}
          >
            {calEstado === 'publicado' ? '✓ Publicado' : calEstado ? '⚠ Borrador' : '— Sin planificar'}
          </span>
        ))}

        {/* Entrada / salida del modo edición */}
        {esAdmin && vista === 'calendario' && (
          <button
            onClick={() => modoEdicion ? salirEdicion() : setModoEdicion(true)}
            style={{
              background: modoEdicion ? 'white' : 'rgba(255,255,255,0.2)',
              border: modoEdicion ? 'none' : '1.5px solid rgba(255,255,255,0.45)',
              borderRadius: 20, padding: '6px 16px',
              color: modoEdicion ? '#0f6e56' : 'white',
              fontWeight: 700, fontSize: 12, cursor: 'pointer',
            }}
          >
            {modoEdicion ? '✓ Listo' : '✏️ Editar planificación'}
          </button>
        )}
      </div>

      {/* Técnico de turno de emergencias (admin lo gestiona desde aquí) */}
      <TurnoEmergencia puedeGestionar={esAdmin} compacto />

      {/* Controles admin — en la vista Calendario solo en modo edición */}
      {esAdmin && (modoEdicion || vista !== 'calendario') && (
        <div style={{
          background: '#0B0F0D', padding: '10px 16px',
          borderBottom: '1px solid #1E2922', display: 'flex',
          gap: 8, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <button
            onClick={() => copiarMesAnterior(false)}
            disabled={generando}
            style={btnTool(generando ? '#6B7C74' : '#1E1228', '#C4B5FD', '1.5px solid #4C1D95')}
          >
            ⧉ Copiar mes anterior
          </button>
          <button
            onClick={() => doGenerar(false)}
            disabled={generando}
            style={btnTool(generando ? '#6B7C74' : '#0f6e56', 'white')}
          >
            {generando ? 'Procesando…' : '⚡ Generar'}
          </button>
          <button
            onClick={abrirAvisos}
            title="Enviar la programación del mes a los clientes marcados"
            style={btnTool(nAvisoPendiente > 0 ? '#1C1200' : '#161C19',
                           nAvisoPendiente > 0 ? '#F59E0B' : '#8A9994',
                           `1.5px solid ${nAvisoPendiente > 0 ? '#F59E0B' : '#2E4A40'}`)}
          >
            ✉ Avisar clientes{nAvisoPendiente > 0 ? ` (${nAvisoPendiente})` : ''}
          </button>
          <button
            onClick={() => setModalResponsables(true)}
            title="Técnico responsable de cada comunidad — es lo que usa Generar"
            style={btnTool(nSinResponsable > 0 ? '#1C1200' : '#161C19',
                           nSinResponsable > 0 ? '#F59E0B' : '#8A9994',
                           `1.5px solid ${nSinResponsable > 0 ? '#F59E0B' : '#2E4A40'}`)}
          >
            👥 Responsables{nSinResponsable > 0 ? ` (${nSinResponsable})` : ''}
          </button>
          <button onClick={descargarPlantilla}
            style={btnTool('#161C19', '#4CAF50', '1.5px solid #0f6e56')}>
            📥 Plantilla
          </button>
          <input ref={fileImport} type="file" accept=".xlsx"
            onChange={cargarPlantilla} style={{ display: 'none' }} />
          <button
            onClick={() => fileImport.current.click()}
            disabled={importando}
            style={btnTool(importando ? '#6B7C74' : '#0D1E2E', '#38BDF8')}
          >
            {importando ? 'Importando…' : '📤 Cargar Excel'}
          </button>
          {vista !== 'calendario' && (
            <select
              value={filtroTecnico}
              onChange={e => setFiltroTecnico(e.target.value)}
              style={{
                padding: '7px 10px', border: '1.5px solid #2E4A40',
                borderRadius: 8, fontSize: 13, background: '#0D1311',
                color: '#F4F7F5', fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              <option value="">Todos los técnicos</option>
              {tecnicos.map(t => (
                <option key={t.id} value={String(t.id)}>{t.nombre}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Buscador global — en la vista Calendario el filtro vive en el panel,
          junto a la lista de comunidades que filtra */}
      {vista !== 'calendario' && (
      <div style={{ background: '#0B0F0D', padding: '10px 16px', borderBottom: '1px solid #1E2922' }}>
        <input
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          placeholder="🔍 Buscar comunidad o ascensor..."
          style={{
            width: '100%', padding: '10px 12px', border: '1.5px solid #2E4A40',
            borderRadius: 10, fontSize: 14, boxSizing: 'border-box',
            fontFamily: 'inherit', outline: 'none',
            background: '#0D1311', color: '#F4F7F5',
          }}
        />
      </div>
      )}

      {/* Advertencias */}
      {advertencias.length > 0 && (
        <div style={{ background: '#1C1200', borderBottom: '2px solid #F59E0B', padding: '10px 16px' }}>
          <p style={{ margin: '0 0 6px', fontWeight: 700, color: '#F59E0B', fontSize: 13 }}>
            ⚠ {advertencias.length} advertencia{advertencias.length > 1 ? 's' : ''}:
          </p>
          {advertencias.slice(0, 8).map((a, i) => (
            <p key={i} style={{ margin: '2px 0', fontSize: 12, color: '#F59E0B' }}>· {a}</p>
          ))}
          {advertencias.length > 8 && (
            <p style={{ margin: '2px 0', fontSize: 12, color: '#F59E0B' }}>
              y {advertencias.length - 8} más...
            </p>
          )}
          <button onClick={() => setAdvertencias([])}
            style={{ marginTop: 6, fontSize: 11, color: '#F59E0B',
              background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            Cerrar
          </button>
        </div>
      )}

      {/* La lista son tarjetas: a 1400px quedarían estiradas, así que se acota
          y se centra. El calendario y la grilla sí aprovechan todo el ancho. */}
      <div
        className="page-content"
        style={{
          padding: vista === 'lista' ? 12 : 0,
          width: '100%',
          maxWidth: vista === 'lista' && esPC ? 900 : undefined,
          margin: vista === 'lista' && esPC ? '0 auto' : undefined,
        }}
      >
        {loading ? (
          <p style={{ textAlign: 'center', color: '#6B7C74', padding: 40 }}>Cargando...</p>

        // ── Vista Calendario clicable ─────────────────────────────────────────
        ) : vista === 'calendario' && esAdmin ? (
          !tablero ? (
            <p style={{ textAlign: 'center', color: '#6B7C74', padding: 40 }}>Sin datos</p>
          ) : (
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start',
            flexWrap: 'wrap', padding: 12 }}>

            {/* Panel izquierdo: técnicos + comunidades. Solo en modo edición;
                en lectura el calendario se queda con todo el ancho. */}
            {modoEdicion && (
            <div style={{ flex: '1 1 230px', minWidth: 210, maxWidth: esPC ? 350 : 320 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7C74',
                textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                1 · Técnico
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                {/* Muestra de color del técnico activo: es el color con el que
                    se pintan sus visitas en el calendario */}
                <span style={{
                  width: 14, height: 14, borderRadius: 4, flexShrink: 0,
                  background: colorDeTecnico(tecnicoSel).fg,
                }} />
                <select
                  value={tecnicoSel == null ? '' : String(tecnicoSel)}
                  onChange={e => setTecnicoSel(e.target.value ? parseInt(e.target.value) : null)}
                  style={{
                    flex: 1, minWidth: 0, padding: '8px 10px',
                    border: '1.5px solid #2E4A40', borderRadius: 8,
                    fontSize: 13, fontWeight: 700, background: '#0D1311',
                    color: colorDeTecnico(tecnicoSel).fg,
                    fontFamily: 'inherit', cursor: 'pointer',
                  }}
                >
                  <option value="">Sin asignar</option>
                  {tablero.tecnicos.map(t => (
                    <option key={t.id} value={String(t.id)}>{t.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Leyenda: qué color corresponde a cada técnico en el calendario.
                  Clic en cualquiera abre el editor de colores. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px',
                marginBottom: 14, alignItems: 'center' }}>
                {tablero.tecnicos.map(t => {
                  const hex = hexDeTecnico(t.id);
                  return (
                    <button
                      key={t.id}
                      onClick={() => setModalColores(true)}
                      title="Cambiar colores de los técnicos"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 10, color: tecnicoSel === t.id ? hex : '#6B7C74',
                        fontWeight: tecnicoSel === t.id ? 700 : 500,
                      }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: hex }} />
                      {t.nombre}
                    </button>
                  );
                })}
                <button
                  onClick={() => setModalColores(true)}
                  title="Cambiar colores de los técnicos"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#6B7C74', fontSize: 11, padding: 0, lineHeight: 1,
                  }}
                >
                  🎨
                </button>
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7C74',
                textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                2 · Comunidad {comunidadSel && '— clic en los días'}
              </div>
              {/* Buscador junto a la lista que filtra, no en la barra superior */}
              <div style={{ position: 'relative', marginBottom: 6 }}>
                <input
                  value={busquedaCom}
                  onChange={e => setBusquedaCom(e.target.value)}
                  placeholder="🔍 Filtrar comunidad..."
                  style={{
                    width: '100%', padding: '8px 30px 8px 10px',
                    border: '1.5px solid #2E4A40', borderRadius: 8,
                    fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit',
                    outline: 'none', background: '#0D1311', color: '#F4F7F5',
                  }}
                />
                {busquedaCom && (
                  <button
                    onClick={() => setBusquedaCom('')}
                    title="Limpiar"
                    style={{
                      position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', color: '#8A9994',
                      fontSize: 14, cursor: 'pointer', padding: '2px 4px', lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>

              <div style={{
                maxHeight: esPC ? 620 : 460, overflowY: 'auto',
                border: '1px solid #1E2922', borderRadius: 10, background: '#0D1311',
              }}>
                {comunidadesPanel.map(c => {
                  const asignados = diasPorComunidad[c.nombre] || 0;
                  const req  = c.dias_mantencion || 1;
                  const sel  = comunidadSel === c.nombre;
                  const ok   = asignados === req;
                  const over = asignados > req;
                  // Estado del aviso: gris no notifica · ámbar pendiente · verde enviado
                  const avisoColor = !c.notificar_email ? '#3A443F'
                    : c.avisado_en ? '#4CAF50' : '#F59E0B';
                  const avisoTitulo = !c.notificar_email
                    ? 'No recibe aviso de programación — clic para activar'
                    : c.avisado_en
                      ? `Aviso enviado el ${fechaCL(c.avisado_en)} — clic para desactivar`
                      : 'Pendiente de aviso — clic para desactivar';

                  return (
                    <div
                      key={c.nombre}
                      onClick={() => setComunidadSel(sel ? null : c.nombre)}
                      style={{
                        display: 'flex', width: '100%', alignItems: 'center', gap: 6,
                        background: sel ? '#0f6e56' : 'transparent',
                        borderBottom: '1px solid #161C19',
                        padding: '7px 8px', cursor: 'pointer',
                      }}
                    >
                      <span style={{
                        flex: 1, minWidth: 0, fontSize: 12, fontWeight: sel ? 700 : 500,
                        color: sel ? 'white' : '#C9D3CE',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }} title={`${c.nombre} · ${c.n_ascensores} ascensor(es)`}>
                        {nombreCorto(c.nombre)}
                      </span>
                      {c.regla_especial && (
                        <span title={c.regla_especial} style={{ fontSize: 10 }}>📌</span>
                      )}

                      {/* Responsable: color del técnico, o hueco si no tiene */}
                      <span
                        title={c.responsable_id
                          ? `Responsable: ${tablero.tecnicos.find(t => t.id === c.responsable_id)?.nombre || '—'}`
                          : 'Sin técnico responsable'}
                        style={{
                          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                          background: c.responsable_id ? hexDeTecnico(c.responsable_id) : 'transparent',
                          border: c.responsable_id ? 'none' : '1px dashed #4A5551',
                        }}
                      />

                      {/* Marca si la comunidad recibe el aviso mensual por correo */}
                      <button
                        onClick={e => { e.stopPropagation(); toggleNotificar(c.nombre, !c.notificar_email); }}
                        title={avisoTitulo}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: avisoColor, fontSize: 12, padding: '2px 3px', lineHeight: 1,
                        }}
                      >
                        ✉
                      </button>

                      {/* Ajustar qué equipos van cada día (solo si tiene 2+ días) */}
                      {asignados > 1 && (
                        <button
                          onClick={e => { e.stopPropagation(); abrirReparto(c.nombre); }}
                          title="Ajustar qué ascensores van cada día"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#8A9994', fontSize: 11, padding: '2px 3px', lineHeight: 1,
                          }}
                        >
                          ⚙
                        </button>
                      )}

                      <span style={{
                        fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '2px 7px',
                        background: over ? '#2A1410' : ok ? '#12382D' : '#1C1200',
                        color:      over ? '#FB7185' : ok ? '#4CAF50' : '#F59E0B',
                      }}>
                        {asignados}/{req}
                      </span>
                    </div>
                  );
                })}
                {comunidadesPanel.length === 0 && (
                  <p style={{ padding: 16, fontSize: 12, color: '#6B7C74', textAlign: 'center' }}>
                    Sin resultados
                  </p>
                )}
              </div>

              {comunidadSel && (
                <p style={{ fontSize: 11, color: '#8A9994', marginTop: 8, lineHeight: 1.5 }}>
                  Clic en un día lo agrega · clic de nuevo lo quita ·
                  con otro técnico activo, lo reasigna.
                </p>
              )}
            </div>
            )}

            {/* Calendario mensual */}
            <div style={{ flex: modoEdicion ? '3 1 460px' : '1 1 100%', minWidth: 300 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: esPC ? 6 : 4 }}>
                {CABECERA_SEMANA.map(d => (
                  <div key={d} style={{
                    textAlign: 'center', fontSize: esPC ? 12.5 : 11, fontWeight: 700,
                    color: d === 'Dom' ? '#4A5551' : '#6B7C74', padding: '4px 0',
                  }}>
                    {d}
                  </div>
                ))}

                {semanas.flat().map((dia, idx) => {
                  if (!dia) return <div key={`v${idx}`} />;

                  const asigs = porDia[dia.fecha] || [];
                  const sobrecarga = asigs.some(
                    a => a.tecnico_id && cargaTecnicoDia[`${a.tecnico_id}|${dia.fecha}`] > MAX_COM_DIA
                  );
                  const yaTiene = comunidadSel &&
                    asigs.some(a => a.comunidad === comunidadSel);
                  const esHoy = dia.fecha === hoy;

                  return (
                    <div
                      key={dia.fecha}
                      onClick={() => clicDia(dia.fecha, dia.clicable)}
                      title={
                        !dia.clicable
                          ? (dia.feriado ? 'Feriado' : 'Domingo')
                          : !modoEdicion
                            ? (esHoy ? 'Hoy' : undefined)
                            : comunidadSel
                              ? (yaTiene ? `Quitar ${comunidadSel}` : `Agregar ${comunidadSel}`)
                              : 'Selecciona una comunidad para asignar'
                      }
                      style={{
                        minHeight: esPC ? (modoEdicion ? 124 : 136) : 82,
                        background: !dia.clicable ? '#0A0D0B'
                          : esHoy ? '#13201B' : '#111714',
                        border: `1.5px solid ${
                          yaTiene ? '#0f6e56'
                          : esHoy ? '#4ADE80'
                          : sobrecarga ? 'rgba(245,158,11,0.55)'
                          : 'rgba(255,255,255,0.06)'
                        }`,
                        borderRadius: 8, padding: esPC ? 6 : 4,
                        cursor: modoEdicion && dia.clicable && comunidadSel ? 'pointer' : 'default',
                        opacity: dia.clicable ? 1 : 0.45,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: 3, gap: 4 }}>
                        <span style={{
                          fontSize: esPC ? 13 : 11, fontWeight: 700,
                          color: esHoy ? '#4ADE80'
                            : dia.feriado ? '#FB7185'
                            : dia.dow === 6 ? '#38BDF8' : '#8A9994',
                        }}>
                          {dia.dia}
                        </span>
                        {esHoy && (
                          <span style={{
                            fontSize: 8.5, fontWeight: 700, color: '#0B0F0D',
                            background: '#4ADE80', borderRadius: 8, padding: '1px 6px',
                            letterSpacing: 0.3,
                          }}>
                            HOY
                          </span>
                        )}
                        <span style={{ flex: 1 }} />
                        {sobrecarga && <span title="Más de 2 comunidades para un técnico"
                          style={{ fontSize: 10 }}>⚠</span>}
                      </div>

                      {asigs.map(a => {
                        const col = colorDeTecnico(a.tecnico_id);
                        const hecha = a.n_completados > 0;
                        return (
                          <div
                            key={`${a.comunidad}|${a.fecha}`}
                            title={`${a.comunidad}${a.tecnico_nombre ? ' · ' + a.tecnico_nombre : ' · sin técnico'}` +
                              (hecha ? ` · ${a.n_completados}/${a.n_ascensores} listos` : '')}
                            style={{
                              background: col.bg, color: col.fg,
                              border: a.pendiente ? `1px dashed ${col.fg}` : '1px solid transparent',
                              borderRadius: 4, padding: esPC ? '3px 6px' : '2px 4px', marginBottom: 2,
                              fontSize: esPC ? 11.5 : 9.5, fontWeight: 700, lineHeight: 1.35,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                          >
                            {hecha && '✓ '}{nombreCorto(a.comunidad)}
                            {esPC && a.equipos?.length > 0 && (
                              <span style={{ opacity: 0.75, fontWeight: 500 }}>
                                {' · '}{resumirEquipos(a.equipos)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Resumen del mes: avance y carga por técnico */}
              {cargaPorTecnico.length > 0 && (
                <div style={{
                  marginTop: 14, padding: 12, background: '#0D1311',
                  border: '1px solid #1E2922', borderRadius: 10,
                }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px',
                    alignItems: 'baseline', marginBottom: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7C74',
                      textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Carga del mes
                    </span>
                    <span style={{ fontSize: 12, color: '#8A9994' }}>
                      {avanceMes.total} visita{avanceMes.total === 1 ? '' : 's'}
                      {' · '}
                      <span style={{ color: '#4CAF50' }}>{avanceMes.hechas} hecha{avanceMes.hechas === 1 ? '' : 's'}</span>
                      {avanceMes.sinTec > 0 && (
                        <>
                          {' · '}
                          <span style={{ color: '#FB7185', fontWeight: 700 }}>
                            {avanceMes.sinTec} sin técnico
                          </span>
                        </>
                      )}
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {cargaPorTecnico.map(c => {
                      const col = colorDeTecnico(c.tecnico_id);
                      const sinTec = c.tecnico_id === null;
                      return (
                        <div
                          key={c.tecnico_id ?? 'sin'}
                          title={`${c.nombre}: ${c.visitas} visita(s) en ${c.dias} día(s)`}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            background: sinTec ? 'rgba(251,113,133,0.12)' : col.bg,
                            border: `1px solid ${sinTec ? '#FB7185' : col.fg}`,
                            borderRadius: 8, padding: '5px 10px',
                          }}
                        >
                          <span style={{
                            width: 8, height: 8, borderRadius: 2, flexShrink: 0,
                            background: sinTec ? '#FB7185' : col.fg,
                          }} />
                          <span style={{
                            fontSize: 11.5, fontWeight: 700,
                            color: sinTec ? '#FB7185' : col.fg,
                          }}>
                            {c.nombre}
                          </span>
                          <span style={{ fontSize: 11, color: '#8A9994' }}>
                            {c.visitas} vis · {c.dias} día{c.dias === 1 ? '' : 's'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {sinCobertura.length > 0 && (
                <button
                  onClick={abrirCobertura}
                  style={{
                    marginTop: 12, display: 'flex', alignItems: 'center', gap: 8,
                    background: nCobPendiente > 0 ? 'rgba(245,158,11,0.1)' : 'rgba(76,175,80,0.08)',
                    border: `1px solid ${nCobPendiente > 0 ? '#F59E0B' : '#2E4A40'}`,
                    borderRadius: 10, padding: '9px 12px', cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 12, textAlign: 'left',
                    color: nCobPendiente > 0 ? '#F59E0B' : '#8A9994',
                  }}
                >
                  {nCobPendiente > 0 ? '⚠' : '✓'}
                  <span>
                    {sinCobertura.length} comunidad{sinCobertura.length > 1 ? 'es' : ''} sin
                    visita publicada este mes
                    {nCobPendiente > 0
                      ? ` · ${nCobPendiente} sin justificar`
                      : ' · todas justificadas'}
                  </span>
                  <span style={{ textDecoration: 'underline', opacity: 0.8 }}>Revisar</span>
                </button>
              )}
            </div>
          </div>
          )

        // ── Vista Grilla ──────────────────────────────────────────────────────
        ) : vista === 'grilla' && esAdmin ? (
          <div style={{ overflowX: 'auto', fontSize: 12 }}>
            <table style={{ borderCollapse: 'collapse', minWidth: '100%', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{
                    position: 'sticky', left: 0, background: '#0f6e56', color: 'white',
                    padding: '8px 10px', textAlign: 'left', zIndex: 2,
                    minWidth: 130, borderRight: '2px solid #0a4d3c',
                  }}>
                    Técnico
                  </th>
                  {diasEnMes.map(d => {
                    const finde = d.dow === 0 || d.dow === 6;
                    return (
                      <th key={d.num} style={{
                        padding: '4px 3px', background: finde ? '#1C2922' : '#0f6e56',
                        color: finde ? '#8A9994' : 'white', textAlign: 'center',
                        minWidth: 38, fontSize: 10, borderRight: '1px solid rgba(255,255,255,0.08)',
                      }}>
                        <div>{DIAS_CORTOS[d.dow]}</div>
                        <div style={{ fontWeight: 400 }}>{d.num}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {tecnicosUnicos.map((tec, ti) => (
                  <tr key={tec} style={{ background: ti % 2 === 0 ? '#161C19' : '#111714' }}>
                    <td style={{
                      position: 'sticky', left: 0, background: ti % 2 === 0 ? '#161C19' : '#111714',
                      padding: '6px 10px', fontWeight: 600, fontSize: 12, color: '#F4F7F5',
                      borderRight: '2px solid #1E2922', zIndex: 1,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130,
                    }}>
                      {tec}
                    </td>
                    {diasEnMes.map(d => {
                      const coms = grilla[tec]?.[d.num] || [];
                      const finde = d.dow === 0 || d.dow === 6;
                      return (
                        <td key={d.num} style={{
                          padding: '3px 2px', verticalAlign: 'top', textAlign: 'center',
                          background: coms.length > MAX_COM_DIA ? '#2A1410'
                            : coms.length > 0 ? '#12382D'
                            : finde ? '#0D1311' : 'inherit',
                          borderRight: '1px solid rgba(255,255,255,0.04)', minHeight: 30,
                        }}>
                          {coms.map((c, ci) => (
                            <div key={ci} style={{
                              fontSize: 9, color: coms.length > MAX_COM_DIA ? '#FB7185' : '#4CAF50',
                              fontWeight: 600,
                              background: '#0a2e20', borderRadius: 3, padding: '1px 3px',
                              marginBottom: 1, lineHeight: 1.3,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              maxWidth: 36,
                            }} title={c}>
                              {nombreCorto(c).slice(0, 6)}
                            </div>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {tecnicosUnicos.length === 0 && (
                  <tr>
                    <td colSpan={diasEnMes.length + 1} style={{ textAlign: 'center', padding: 32, color: '#6B7C74' }}>
                      Sin datos para este período
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        // ── Vista Lista ───────────────────────────────────────────────────────
        ) : (
          <>
            {sinCobertura.length > 0 && esAdmin && (
              <div style={{
                background: '#1C1200', border: '1.5px solid rgba(245,158,11,0.4)',
                borderRadius: 12, padding: 14, marginBottom: 14,
              }}>
                <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#F59E0B', fontSize: 13 }}>
                  ⚠ {sinCobertura.length} comunidad{sinCobertura.length > 1 ? 'es' : ''} sin visita programada
                </p>
                {sinCobertura.slice(0, 5).map((a, i) => (
                  <p key={i} style={{ margin: '2px 0', fontSize: 12, color: '#F59E0B' }}>· {a}</p>
                ))}
                {sinCobertura.length > 5 && (
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#F59E0B' }}>
                    y {sinCobertura.length - 5} más...
                  </p>
                )}
              </div>
            )}

            {itemsFiltrados.length === 0 && (
              <p style={{ textAlign: 'center', color: '#6B7C74', padding: 32 }}>
                Sin visitas programadas para este período
              </p>
            )}

            {itemsFiltrados.map(item => {
              const est = ESTADO_STYLE[item.estado] || ESTADO_STYLE.pendiente;
              const isEditing = editandoId === item.id;

              return (
                <div key={item.id} style={{
                  background: '#161C19', borderRadius: 14, padding: 14,
                  marginBottom: 10, border: '1px solid rgba(255,255,255,0.07)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#F4F7F5' }}>
                        {item.comunidad}
                        {item.override_manual && (
                          <span title="Fecha editada manualmente" style={{ marginLeft: 6, fontSize: 12 }}>🔒</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#8A9994', marginTop: 2 }}>
                        {item.label}{item.n_ascensor ? ` · N° ${item.n_ascensor}` : ''}
                        {item.tecnico_nombre && (
                          <span style={{ marginLeft: 6, color: '#6B7C74' }}>· {item.tecnico_nombre}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                      {esAdmin && (
                        <button
                          onClick={() => isEditing ? setEditandoId(null) : abrirEditor(item)}
                          title="Editar fecha"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#8A9994', fontSize: 14, padding: '2px 4px',
                          }}
                        >
                          ✏️
                        </button>
                      )}
                      <span style={{
                        ...est, borderRadius: 20, padding: '3px 10px',
                        fontSize: 11, fontWeight: 700,
                      }}>
                        {item.estado}
                      </span>
                    </div>
                  </div>

                  {isEditing ? (
                    <div ref={editRef} style={{
                      display: 'flex', gap: 8, alignItems: 'center',
                      marginTop: 8, padding: '8px', background: '#0D1311',
                      borderRadius: 8, flexWrap: 'wrap',
                      border: '1px solid #2E4A40',
                    }}>
                      <input
                        type="date"
                        value={editFecha}
                        onChange={e => setEditFecha(e.target.value)}
                        style={{
                          border: '1.5px solid #0f6e56', borderRadius: 6,
                          padding: '5px 8px', fontSize: 13, fontFamily: 'inherit',
                          background: '#0B0F0D', color: '#F4F7F5',
                        }}
                      />
                      <button
                        onClick={() => guardarFecha(item.id)}
                        style={{
                          background: '#0f6e56', color: 'white', border: 'none',
                          borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
                          fontSize: 13, fontWeight: 700,
                        }}
                      >
                        Guardar
                      </button>
                      <button
                        onClick={() => setEditandoId(null)}
                        style={{
                          background: '#161C19', color: '#C9D3CE',
                          border: '1px solid rgba(255,255,255,0.12)',
                          borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 13,
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#8A9994' }}>
                      📅 {item.fecha_programada ? formatFecha(item.fecha_programada) : '—'}
                      {item.hora_inicio && ` · ${item.hora_inicio}`}
                    </div>
                  )}

                  {item.observaciones && !isEditing && (
                    <div style={{ fontSize: 12, color: '#6B7C74', marginTop: 6,
                      fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 6 }}>
                      {item.observaciones}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* ── Modal: avisos de programación al cliente ─────────────────────── */}
      {modalAvisos && avisos && (
        <div
          onClick={() => !enviandoAvisos && setModalAvisos(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#111714', borderRadius: 16, width: '100%',
              maxWidth: vistaPrevia ? 900 : 620, maxHeight: '90vh',
              display: 'flex', flexDirection: 'column',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <div style={{
              padding: '14px 18px', borderBottom: '1px solid #1E2922',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <h2 style={{ margin: 0, fontSize: 15, color: '#F4F7F5', flex: 1 }}>
                ✉ Avisar programación — {MESES[mes - 1]} {anio}
              </h2>
              <button
                onClick={() => setModalAvisos(false)}
                style={{ background: 'none', border: 'none', color: '#8A9994',
                  fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            {vistaPrevia ? (
              <>
                <div style={{ padding: '10px 18px', borderBottom: '1px solid #1E2922' }}>
                  <p style={{ margin: 0, fontSize: 12, color: '#8A9994' }}>
                    Vista previa de <strong style={{ color: '#C9D3CE' }}>{vistaPrevia.comunidad}</strong>
                    {' · '}a: {vistaPrevia.destinatarios.join(', ')}
                  </p>
                </div>
                <div style={{ flex: 1, overflow: 'auto', padding: 12, background: '#fff' }}>
                  <iframe
                    title="Vista previa del correo"
                    srcDoc={vistaPrevia.html}
                    style={{ width: '100%', height: 460, border: 'none', background: '#fff' }}
                  />
                </div>
                <div style={{ padding: '12px 18px', borderTop: '1px solid #1E2922',
                  display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setVistaPrevia(null)}
                    style={btnTool('#161C19', '#C9D3CE', '1px solid rgba(255,255,255,0.12)')}
                  >
                    ← Volver
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ padding: '12px 18px', borderBottom: '1px solid #1E2922',
                  display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 12, color: '#8A9994' }}>
                    Horario{' '}
                    <input
                      type="time" value={horaInicio}
                      onChange={e => setHoraInicio(e.target.value)}
                      style={{ background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit',
                        border: '1.5px solid #2E4A40', borderRadius: 6, padding: '4px 6px', fontSize: 12 }}
                    />
                    {' a '}
                    <input
                      type="time" value={horaFin}
                      onChange={e => setHoraFin(e.target.value)}
                      style={{ background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit',
                        border: '1.5px solid #2E4A40', borderRadius: 6, padding: '4px 6px', fontSize: 12 }}
                    />
                  </label>
                  <span style={{ flex: 1 }} />
                  <button
                    onClick={() => setAvisosSel(
                      avisosSel.length === avisos.comunidades.filter(c => !c.sin_email).length
                        ? []
                        : avisos.comunidades.filter(c => !c.sin_email).map(c => c.nombre)
                    )}
                    style={{ background: 'none', border: 'none', color: '#38BDF8',
                      fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Marcar / desmarcar todas
                  </button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
                  {avisos.comunidades.length === 0 && (
                    <p style={{ padding: 28, textAlign: 'center', color: '#6B7C74', fontSize: 13, lineHeight: 1.6 }}>
                      Ninguna comunidad de este mes está marcada para recibir aviso.<br />
                      Actívalas con el ✉ en la lista de comunidades.
                    </p>
                  )}
                  {avisos.comunidades.map(c => {
                    const sel = avisosSel.includes(c.nombre);
                    return (
                      <div
                        key={c.nombre}
                        onClick={() => {
                          if (c.sin_email) return;
                          setAvisosSel(s => sel ? s.filter(x => x !== c.nombre) : [...s, c.nombre]);
                        }}
                        style={{
                          display: 'flex', gap: 10, padding: '10px 18px',
                          borderBottom: '1px solid #161C19',
                          cursor: c.sin_email ? 'not-allowed' : 'pointer',
                          opacity: c.sin_email ? 0.5 : 1,
                          background: sel ? 'rgba(15,110,86,0.18)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox" checked={sel} disabled={c.sin_email} readOnly
                          style={{ marginTop: 2, accentColor: '#0f6e56', cursor: 'inherit' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#F4F7F5' }}>
                            {c.nombre}
                            {c.avisado_en && (
                              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700,
                                background: '#12382D', color: '#4CAF50',
                                borderRadius: 10, padding: '2px 7px' }}>
                                ya avisada {fechaCL(c.avisado_en)}
                              </span>
                            )}
                          </div>
                          {c.dias.map(d => (
                            <div key={d.fecha} style={{ fontSize: 11, color: '#8A9994', marginTop: 2 }}>
                              {formatFecha(d.fecha)} · Ascensor {resumirEquipos(d.equipos)}
                              {' · '}{d.tecnico || <span style={{ color: '#F59E0B' }}>sin técnico</span>}
                            </div>
                          ))}
                          <div style={{ fontSize: 10, color: c.sin_email ? '#FB7185' : '#4A5551', marginTop: 3 }}>
                            {c.sin_email
                              ? '⚠ Sin correos de cliente cargados — no se puede enviar'
                              : c.emails.join(', ')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ padding: '12px 18px', borderTop: '1px solid #1E2922',
                  display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ flex: 1, fontSize: 12, color: '#8A9994' }}>
                    {avisosSel.length} seleccionada{avisosSel.length === 1 ? '' : 's'}
                  </span>
                  <button
                    onClick={() => enviarAvisos(true)}
                    disabled={enviandoAvisos || !avisosSel.length}
                    style={btnTool('#161C19', '#38BDF8', '1.5px solid #0D3A52')}
                  >
                    👁 Vista previa
                  </button>
                  <button
                    onClick={() => enviarAvisos(false)}
                    disabled={enviandoAvisos || !avisosSel.length}
                    style={btnTool(enviandoAvisos ? '#6B7C74' : '#0f6e56', 'white')}
                  >
                    {enviandoAvisos ? 'Enviando…' : `✉ Enviar (${avisosSel.length})`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Modal: técnico responsable por comunidad ──────────────────────── */}
      {modalResponsables && tablero && (
        <div
          onClick={() => setModalResponsables(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#111714', borderRadius: 16, width: '100%', maxWidth: 600,
              maxHeight: '90vh', display: 'flex', flexDirection: 'column',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #1E2922',
              display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 15, color: '#F4F7F5', flex: 1 }}>
                👥 Técnico responsable por comunidad
              </h2>
              <button
                onClick={() => setModalResponsables(false)}
                style={{ background: 'none', border: 'none', color: '#8A9994',
                  fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '12px 18px', borderBottom: '1px solid #1E2922' }}>
              <p style={{ margin: 0, fontSize: 12, color: '#8A9994', lineHeight: 1.6 }}>
                Es lo que usa <strong style={{ color: '#C9D3CE' }}>⚡ Generar</strong> para
                asignar. Una comunidad sin responsable genera visitas sin técnico, y esas
                no le aparecen a nadie en la app.
              </p>
              {nSinResponsable > 0 && (
                <p style={{ margin: '8px 0 0', fontSize: 12, color: '#F59E0B', fontWeight: 700 }}>
                  ⚠ {nSinResponsable} de {tablero.comunidades.length} sin responsable
                </p>
              )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {tablero.comunidades.map(c => {
                const hex = c.responsable_id ? hexDeTecnico(c.responsable_id) : null;
                return (
                  <div key={c.nombre} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 18px', borderBottom: '1px solid #161C19',
                  }}>
                    <span style={{
                      width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                      background: hex || 'transparent',
                      border: hex ? 'none' : '1px dashed #4A5551',
                    }} />
                    <span style={{
                      flex: 1, minWidth: 0, fontSize: 12.5, color: '#C9D3CE',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={`${c.nombre} · ${c.n_ascensores} ascensor(es)`}>
                      {nombreCorto(c.nombre)}
                    </span>
                    <select
                      value={c.responsable_id == null ? '' : String(c.responsable_id)}
                      onChange={e => cambiarResponsable(
                        c.nombre, e.target.value ? parseInt(e.target.value) : null
                      )}
                      style={{
                        width: 170, padding: '5px 8px', fontSize: 12,
                        border: `1.5px solid ${c.responsable_id ? '#2E4A40' : '#F59E0B'}`,
                        borderRadius: 7, background: '#0D1311',
                        color: hex || '#F59E0B',
                        fontFamily: 'inherit', fontWeight: 700, cursor: 'pointer',
                      }}
                    >
                      <option value="">— Sin asignar —</option>
                      {tablero.tecnicos.map(t => (
                        <option key={t.id} value={String(t.id)}>{t.nombre}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: '12px 18px', borderTop: '1px solid #1E2922',
              display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalResponsables(false)}
                style={btnTool('#0f6e56', 'white')}>
                Listo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: comunidades sin visita y su justificación ──────────────── */}
      {modalCobertura && (
        <div
          onClick={() => setModalCobertura(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#111714', borderRadius: 16, width: '100%', maxWidth: 600,
              maxHeight: '90vh', display: 'flex', flexDirection: 'column',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #1E2922',
              display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 15, color: '#F4F7F5', flex: 1 }}>
                Sin visita en {MESES[mes - 1]} {anio}
              </h2>
              <button
                onClick={() => setModalCobertura(false)}
                style={{ background: 'none', border: 'none', color: '#8A9994',
                  fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '12px 18px', borderBottom: '1px solid #1E2922' }}>
              <p style={{ margin: 0, fontSize: 12, color: '#8A9994', lineHeight: 1.6 }}>
                Deja constancia de por qué quedó fuera (contrato suspendido, edificio en
                obra, cliente reagendó…). El motivo queda guardado para este mes, así el
                próximo se vuelve a preguntar.
              </p>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
              {(cobertura?.sin_cobertura || []).map(com => {
                const guardada = cobertura?.justificaciones?.[com];
                const texto    = motivoEdit[com] ?? '';
                const sucio    = texto.trim() !== (guardada?.motivo || '');
                return (
                  <div key={com} style={{
                    marginBottom: 10, padding: 12, borderRadius: 10,
                    background: '#0D1311',
                    border: `1px solid ${guardada ? '#2E4A40' : 'rgba(245,158,11,0.5)'}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: '#F4F7F5', flex: 1 }}>
                        {com}
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '2px 8px',
                        background: guardada ? '#12382D' : '#1C1200',
                        color: guardada ? '#4CAF50' : '#F59E0B',
                      }}>
                        {guardada ? '✓ justificada' : 'sin justificar'}
                      </span>
                    </div>

                    <textarea
                      value={texto}
                      onChange={e => setMotivoEdit(m => ({ ...m, [com]: e.target.value }))}
                      placeholder="Motivo por el que no se planificó este mes..."
                      rows={2}
                      style={{
                        width: '100%', boxSizing: 'border-box', resize: 'vertical',
                        padding: '7px 9px', fontSize: 12, fontFamily: 'inherit',
                        border: '1.5px solid #2E4A40', borderRadius: 7,
                        background: '#0B0F0D', color: '#F4F7F5', outline: 'none',
                      }}
                    />

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                      <span style={{ flex: 1, fontSize: 10.5, color: '#6B7C74' }}>
                        {guardada
                          ? `Por ${guardada.por || '—'}${guardada.fecha ? ` · ${fechaCL(guardada.fecha)}` : ''}`
                          : ''}
                      </span>
                      {guardada && !texto.trim() && (
                        <span style={{ fontSize: 10.5, color: '#FB7185' }}>
                          Guardar vacío borra el motivo
                        </span>
                      )}
                      <button
                        onClick={() => guardarMotivo(com)}
                        disabled={!sucio || motivoGuardando === com}
                        style={{
                          ...btnTool(
                            !sucio ? '#161C19' : motivoGuardando === com ? '#6B7C74' : '#0f6e56',
                            !sucio ? '#4A5551' : 'white'
                          ),
                          padding: '5px 12px', fontSize: 12,
                          cursor: sucio ? 'pointer' : 'default',
                        }}
                      >
                        {motivoGuardando === com ? 'Guardando…' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                );
              })}

              {(cobertura?.sin_cobertura || []).length === 0 && (
                <p style={{ padding: 24, textAlign: 'center', color: '#4CAF50', fontSize: 13 }}>
                  ✓ Todas las comunidades tienen visita publicada este mes.
                </p>
              )}
            </div>

            <div style={{ padding: '12px 18px', borderTop: '1px solid #1E2922',
              display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalCobertura(false)}
                style={btnTool('#0f6e56', 'white')}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: chequeo antes de publicar ──────────────────────────────── */}
      {modalRevision && revision && (
        <div
          onClick={() => !publicando && setModalRevision(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#111714', borderRadius: 16, width: '100%', maxWidth: 580,
              maxHeight: '90vh', display: 'flex', flexDirection: 'column',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #1E2922',
              display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 15, color: '#F4F7F5', flex: 1 }}>
                Publicar {MESES[mes - 1]} {anio}
              </h2>
              <button
                onClick={() => setModalRevision(false)}
                style={{ background: 'none', border: 'none', color: '#8A9994',
                  fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              <p style={{ margin: '0 0 14px', fontSize: 12.5, color: '#8A9994', lineHeight: 1.6 }}>
                {revision.total_visitas} visita{revision.total_visitas === 1 ? '' : 's'} programada
                {revision.total_visitas === 1 ? '' : 's'}. Al publicar, los técnicos
                ven su ruta en la app.
              </p>

              {revision.listo && (
                <p style={{
                  margin: 0, padding: '12px 14px', fontSize: 13, lineHeight: 1.6,
                  background: 'rgba(76,175,80,0.12)', border: '1px solid #4CAF50',
                  borderRadius: 10, color: '#4CAF50',
                }}>
                  ✓ Sin observaciones: todas las visitas tienen técnico y todas las
                  comunidades están cubiertas.
                </p>
              )}

              {revision.hallazgos.map(h => {
                const bloqueante = h.gravedad === 'bloqueante';
                return (
                  <div key={h.tipo} style={{
                    marginBottom: 10, padding: 12, borderRadius: 10,
                    background: bloqueante ? 'rgba(251,113,133,0.1)' : 'rgba(245,158,11,0.09)',
                    border: `1px solid ${bloqueante ? '#FB7185' : '#F59E0B'}`,
                  }}>
                    <p style={{
                      margin: '0 0 4px', fontSize: 13, fontWeight: 700,
                      color: bloqueante ? '#FB7185' : '#F59E0B',
                    }}>
                      {bloqueante ? '⛔' : '⚠'} {h.titulo}
                    </p>
                    <p style={{ margin: '0 0 8px', fontSize: 11.5, color: '#8A9994', lineHeight: 1.5 }}>
                      {h.detalle}
                    </p>
                    <div style={{ maxHeight: 130, overflowY: 'auto' }}>
                      {h.items.slice(0, 40).map((it, i) => (
                        <p key={i} style={{ margin: '2px 0', fontSize: 11.5, color: '#C9D3CE' }}>
                          · {it}
                        </p>
                      ))}
                      {h.items.length > 40 && (
                        <p style={{ margin: '4px 0 0', fontSize: 11, color: '#6B7C74' }}>
                          y {h.items.length - 40} más…
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: '12px 18px', borderTop: '1px solid #1E2922',
              display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ flex: 1, fontSize: 11.5, color: '#6B7C74', lineHeight: 1.5 }}>
                {revision.bloqueantes > 0
                  ? 'Puedes publicar igual, pero esas visitas no le llegarán a nadie.'
                  : ''}
              </span>
              <button
                onClick={() => setModalRevision(false)}
                style={btnTool('#161C19', '#C9D3CE', '1px solid rgba(255,255,255,0.12)')}
              >
                Volver a corregir
              </button>
              <button
                onClick={() => aplicarEstado('publicado')}
                disabled={publicando}
                style={btnTool(
                  publicando ? '#6B7C74' : revision.bloqueantes > 0 ? '#8A4B2A' : '#0f6e56',
                  'white'
                )}
              >
                {publicando ? 'Publicando…'
                  : revision.bloqueantes > 0 ? 'Publicar así' : 'Publicar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: colores de los técnicos ────────────────────────────────── */}
      {modalColores && tablero && (
        <div
          onClick={() => setModalColores(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#111714', borderRadius: 16, width: '100%', maxWidth: 520,
              maxHeight: '90vh', display: 'flex', flexDirection: 'column',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #1E2922',
              display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 15, color: '#F4F7F5', flex: 1 }}>
                🎨 Colores de los técnicos
              </h2>
              <button
                onClick={() => setModalColores(false)}
                style={{ background: 'none', border: 'none', color: '#8A9994',
                  fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: '#8A9994', lineHeight: 1.6 }}>
                Es el color con el que se pintan sus visitas en el calendario.
                Los tonos ya usados por otro técnico salen marcados para que no
                elijas dos parecidos.
              </p>

              {coloresRepetidos.size > 0 && (
                <p style={{
                  margin: '0 0 14px', padding: '9px 11px', fontSize: 12,
                  background: 'rgba(251,113,133,0.12)', border: '1px solid #FB7185',
                  borderRadius: 8, color: '#FB7185', lineHeight: 1.5,
                }}>
                  ⚠ Hay {coloresRepetidos.size === 1 ? 'un color repetido' : `${coloresRepetidos.size} colores repetidos`}:
                  dos o más técnicos se ven igual en el calendario. Cámbialos abajo
                  — los repetidos están marcados.
                </p>
              )}

              {tablero.tecnicos.map(t => {
                const hex = hexDeTecnico(t.id);
                // Colores en uso por los demás, para no repetir
                const usados = tablero.tecnicos
                  .filter(x => x.id !== t.id)
                  .map(x => (hexDeTecnico(x.id) || '').toUpperCase());

                return (
                  <div key={t.id} style={{
                    marginBottom: 10, padding: 12, background: '#0D1311',
                    borderRadius: 10, border: '1px solid #1E2922',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
                      <span style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0, background: hex,
                      }} />
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#F4F7F5' }}>
                        {t.nombre}
                        {coloresRepetidos.has((hex || '').toUpperCase()) && (
                          <span
                            title="Otro técnico tiene este mismo color"
                            style={{
                              marginLeft: 7, fontSize: 10, fontWeight: 700,
                              background: 'rgba(251,113,133,0.18)', color: '#FB7185',
                              border: '1px solid #FB7185', borderRadius: 10, padding: '1px 7px',
                            }}
                          >
                            ⚠ repetido
                          </span>
                        )}
                      </span>
                      {t.color && (
                        <button
                          onClick={() => cambiarColor(t.id, null)}
                          title="Volver al color automático"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#6B7C74', fontSize: 11, textDecoration: 'underline',
                            padding: 0, fontFamily: 'inherit',
                          }}
                        >
                          automático
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      {PALETA_TEC.map(c => {
                        const activo = (hex || '').toUpperCase() === c.hex.toUpperCase();
                        const ocupado = usados.includes(c.hex.toUpperCase());
                        return (
                          <button
                            key={c.hex}
                            onClick={() => cambiarColor(t.id, c.hex)}
                            title={ocupado ? `${c.nombre} — ya lo usa otro técnico` : c.nombre}
                            style={{
                              width: 26, height: 26, borderRadius: 7, cursor: 'pointer',
                              background: c.hex,
                              border: activo ? '2.5px solid #F4F7F5' : '2px solid transparent',
                              opacity: ocupado && !activo ? 0.3 : 1,
                              position: 'relative', padding: 0,
                            }}
                          >
                            {ocupado && !activo && (
                              <span style={{
                                position: 'absolute', inset: 0, display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontSize: 13, color: '#0B0F0D', fontWeight: 700,
                              }}>
                                ×
                              </span>
                            )}
                          </button>
                        );
                      })}

                      {/* Color libre, para cuando la paleta no alcanza */}
                      <label
                        title="Elegir otro color"
                        style={{
                          width: 26, height: 26, borderRadius: 7, cursor: 'pointer',
                          border: '1.5px dashed #3A443F', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, color: '#6B7C74',
                        }}
                      >
                        +
                        <input
                          type="color"
                          value={hex || '#8A9994'}
                          onChange={e => cambiarColor(t.id, e.target.value.toUpperCase())}
                          style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}

              {tablero.tecnicos.length === 0 && (
                <p style={{ padding: 20, textAlign: 'center', color: '#6B7C74', fontSize: 13 }}>
                  No hay técnicos activos.
                </p>
              )}
            </div>

            <div style={{ padding: '12px 18px', borderTop: '1px solid #1E2922',
              display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setModalColores(false)}
                style={btnTool('#0f6e56', 'white')}
              >
                Listo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: reparto de equipos entre los días ──────────────────────── */}
      {modalReparto && (
        <div
          onClick={() => !guardandoRep && setModalReparto(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#111714', borderRadius: 16, width: '100%', maxWidth: 560,
              maxHeight: '90vh', display: 'flex', flexDirection: 'column',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #1E2922',
              display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 15, color: '#F4F7F5', flex: 1 }}>
                ⚙ Equipos por día — {modalReparto.comunidad}
              </h2>
              <button
                onClick={() => setModalReparto(null)}
                style={{ background: 'none', border: 'none', color: '#8A9994',
                  fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              <p style={{ margin: '0 0 14px', fontSize: 12, color: '#8A9994', lineHeight: 1.6 }}>
                Marca qué ascensores se atienden cada día. Los que ya tienen
                comprobante enviado quedan fijos.
              </p>

              {modalReparto.dias.map(d => (
                <div key={d.fecha} style={{
                  marginBottom: 12, padding: 12, background: '#0D1311',
                  borderRadius: 10, border: '1px solid #1E2922',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#F4F7F5', marginBottom: 8 }}>
                    {formatFecha(d.fecha)}
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: '#6B7C74' }}>
                      {d.ascensor_ids.length} equipo{d.ascensor_ids.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {modalReparto.ascensores.map(a => {
                      const puesto = d.ascensor_ids.includes(a.id);
                      const fijo   = d.completados.includes(a.id);
                      return (
                        <button
                          key={a.id}
                          onClick={() => toggleAscensorDia(d.fecha, a.id)}
                          disabled={fijo}
                          title={fijo ? 'Ya tiene comprobante enviado' : undefined}
                          style={{
                            background: fijo ? '#12382D' : puesto ? '#0f6e56' : '#161C19',
                            color: fijo ? '#4CAF50' : puesto ? 'white' : '#6B7C74',
                            border: `1.5px solid ${fijo ? '#4CAF50' : puesto ? '#0f6e56' : '#2E4A40'}`,
                            borderRadius: 8, padding: '5px 11px', fontSize: 12,
                            fontWeight: 700, cursor: fijo ? 'default' : 'pointer',
                          }}
                        >
                          {fijo && '✓ '}N°{a.n_ascensor ?? '?'}
                          {a.torre ? <span style={{ opacity: 0.7 }}> ·{a.torre}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ padding: '12px 18px', borderTop: '1px solid #1E2922',
              display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => guardarReparto(true)}
                disabled={guardandoRep}
                title="Vuelve al reparto automático por bloques y suelta el ajuste manual"
                style={btnTool('#161C19', '#8A9994', '1px solid rgba(255,255,255,0.12)')}
              >
                ↺ Reparto automático
              </button>
              <span style={{ flex: 1 }} />
              <button
                onClick={() => setModalReparto(null)}
                style={btnTool('#161C19', '#C9D3CE', '1px solid rgba(255,255,255,0.12)')}
              >
                Cancelar
              </button>
              <button
                onClick={() => guardarReparto(false)}
                disabled={guardandoRep}
                style={btnTool(guardandoRep ? '#6B7C74' : '#0f6e56', 'white')}
              >
                {guardandoRep ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barra flotante de guardado */}
      {nPendientes > 0 && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50,
          background: '#0f6e56', padding: '10px 16px',
          display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.45)',
        }}>
          <span style={{ flex: 1, color: 'white', fontSize: 13, fontWeight: 700 }}>
            {nPendientes} cambio{nPendientes > 1 ? 's' : ''} sin guardar
          </span>
          <button
            onClick={() => setPendientes({})}
            style={{
              background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 8,
              padding: '8px 14px', color: 'white', fontSize: 13, cursor: 'pointer',
            }}
          >
            Descartar
          </button>
          <button
            onClick={guardarCambios}
            disabled={guardando}
            style={{
              background: guardando ? '#6B7C74' : 'white', border: 'none', borderRadius: 8,
              padding: '8px 18px', color: '#0f6e56', fontSize: 13, fontWeight: 700,
              cursor: guardando ? 'default' : 'pointer',
            }}
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      )}
    </div>
  );
}
